import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Send, Calculator } from "lucide-react";
import { MaskedLine, Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { CONTACT, SERVICES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUBFLOOR = [
  { id: "good", label: "Good — level & dry", mult: 1 },
  { id: "minor", label: "Minor prep needed", mult: 1.15 },
  { id: "major", label: "Major prep / repair", mult: 1.35 },
];

const round10 = (n) => Math.round(n / 10) * 10;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    area: "",
    message: "",
  });
  const [subfloor, setSubfloor] = useState("good");
  const [sending, setSending] = useState(false);

  const estimate = useMemo(() => {
    const svc = SERVICES.find((s) => s.id === form.service);
    const area = parseFloat(form.area);
    if (!svc || !area || area <= 0) return null;
    const mult = SUBFLOOR.find((s) => s.id === subfloor).mult;
    return `£${round10(area * svc.range[0] * mult).toLocaleString()} – £${round10(
      area * svc.range[1] * mult
    ).toLocaleString()}`;
  }, [form.service, form.area, subfloor]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and a short message.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, estimate: estimate || "" });
      toast.success("Thank you — your enquiry has been received. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", service: "", area: "", message: "" });
      setSubfloor("good");
    } catch {
      toast.error("Something went wrong sending your enquiry. Please try again or call us.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-card border border-border rounded-lg px-4 py-3.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all placeholder:text-muted-foreground/60";

  return (
    <div data-testid="contact-page" className="pt-20">
      <Seo
        title="Contact COC Flooring Ltd — Free Flooring Estimates"
        description="Tell us about your floor. Get an instant guide price and a free site survey from COC Flooring Ltd — call, email or send an enquiry."
      />
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <MaskedLine delay={0.1}>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary font-semibold">
              Contact
            </p>
          </MaskedLine>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight">
            <MaskedLine delay={0.2}>Tell us about</MaskedLine>
            <MaskedLine delay={0.32}>
              your <em className="italic font-light text-primary">floor.</em>
            </MaskedLine>
          </h1>
        </div>
      </section>

      <section className="pb-20 lg:pb-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4 space-y-5">
            {[
              { icon: Phone, label: "Call us", value: CONTACT.phone, href: `tel:${CONTACT.phone.replace(/\s/g, "")}`, testid: "contact-info-phone" },
              { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}`, testid: "contact-info-email" },
              { icon: MapPin, label: "Area served", value: CONTACT.area, testid: "contact-info-area" },
              { icon: Clock, label: "Hours", value: CONTACT.hours, testid: "contact-info-hours" },
            ].map((c, i) => (
              <Reveal key={c.label} delay={i * 0.08}>
                <div className="flex items-center gap-4 bg-card border border-border rounded-xl p-5">
                  <span className="w-11 h-11 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <c.icon size={18} />
                  </span>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                      {c.label}
                    </div>
                    {c.href ? (
                      <a href={c.href} data-testid={c.testid} className="font-medium hover:text-primary transition-colors">
                        {c.value}
                      </a>
                    ) : (
                      <div data-testid={c.testid} className="font-medium">{c.value}</div>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="lg:col-span-8" delay={0.15}>
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="bg-card border border-border rounded-2xl p-8 lg:p-10"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <input
                  data-testid="contact-input-name"
                  className={inputCls}
                  placeholder="Your name *"
                  value={form.name}
                  onChange={set("name")}
                />
                <input
                  data-testid="contact-input-email"
                  type="email"
                  className={inputCls}
                  placeholder="Email address *"
                  value={form.email}
                  onChange={set("email")}
                />
                <input
                  data-testid="contact-input-phone"
                  className={inputCls}
                  placeholder="Phone (optional)"
                  value={form.phone}
                  onChange={set("phone")}
                />
                <select
                  data-testid="contact-select-service"
                  className={inputCls}
                  value={form.service}
                  onChange={set("service")}
                >
                  <option value="">Service of interest…</option>
                  {SERVICES.map((s) => (
                    <option key={s.id} value={s.id}>{s.title}</option>
                  ))}
                </select>
              </div>

              {/* Estimator */}
              <div className="mt-8 border border-dashed border-primary/40 rounded-xl p-6 bg-primary/5">
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-primary font-semibold">
                  <Calculator size={14} /> Instant estimate
                </div>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <input
                    data-testid="estimator-area-input"
                    type="number"
                    min="1"
                    className={inputCls}
                    placeholder="Approx. area (m²)"
                    value={form.area}
                    onChange={set("area")}
                  />
                  <select
                    data-testid="estimator-surface-select"
                    className={inputCls}
                    value={subfloor}
                    onChange={(e) => setSubfloor(e.target.value)}
                  >
                    {SUBFLOOR.map((s) => (
                      <option key={s.id} value={s.id}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div className="mt-4 font-serif text-2xl" data-testid="estimator-result">
                  {estimate ? (
                    <>Estimated range: <span className="text-primary">{estimate}</span></>
                  ) : (
                    <span className="text-muted-foreground text-base font-sans font-light">
                      Choose a service and enter an area for an instant guide price.
                    </span>
                  )}
                </div>
                <p className="mt-2 text-xs text-muted-foreground font-light">
                  Guide price only — a free site survey confirms your exact quote.
                </p>
              </div>

              <textarea
                data-testid="contact-textarea-message"
                rows={5}
                className={`${inputCls} mt-6 resize-none`}
                placeholder="Tell us about your project — rooms, timings, anything that matters… *"
                value={form.message}
                onChange={set("message")}
              />

              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={sending}
                className="mt-8 inline-flex items-center gap-3 bg-primary text-primary-foreground font-mono text-xs uppercase tracking-[0.2em] px-8 py-4 rounded-full hover:bg-[#9A5B1D] transition-colors duration-300 disabled:opacity-60"
              >
                {sending ? "Sending…" : "Send Enquiry"} <Send size={14} />
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
